/**
 * File audit types for search and journey visualization
 * Supports FR-026 through FR-029 from spec 005
 */

// Legacy event types (for backward compatibility)
export type FileEventType =
  | 'RECEIVED'
  | 'PROCESSING'
  | 'VALIDATED'
  | 'ARCHIVED'
  | 'QUARANTINED'
  | 'DELIVERED'
  | 'PICKED_UP'
  | 'FAILED'
  // THUB-149: emitted on the file journey when an outbound file is held because its
  // task is disabled, and again when delivery resumes after the task is re-enabled.
  | 'FILE_HELD'
  | 'DELIVERY_RESUMED'
  // THUB-152: an operator discarded a held file so it will not be delivered. The
  // archived bytes are retained; only the queued delivery is removed.
  | 'HELD_FILE_DISCARDED';

/** File processing status */
export type FileStatus =
  | 'processing'
  | 'ready'
  | 'delivered'
  | 'picked_up'
  | 'failed'
  | 'quarantined';

/** Processing stage types */
export type FileStageType =
  | 'landing'
  | 'processing'
  | 'validation'
  | 'archive'
  | 'notification'
  | 'delivery'
  | 'pickup'
  | 'quarantine';

/** Stage execution status */
export type StageStatus =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'skipped';

/**
 * THUB-205: the exact SNS notification published for a journey event.
 *
 * IMPORTANT — this block is preserved VERBATIM from the API (it is NOT run
 * through snakeToCamel). Rationale:
 *  - `messageAttributes` keys are snake_case by AWS filter-convention
 *    (event_type, partner_id, s3_key ...) — camelCasing them would misrepresent
 *    the exact keys consumers filter on, which is the whole point of the panel.
 *  - `body.metadata` holds user-defined keys that must never be normalized
 *    (CLAUDE.md: "NEVER normalize user-defined data").
 *  - `body` itself is already camelCase on the wire (the exact published SNS
 *    message), so showing it as-is is correct.
 * `notification` is null when the event never published or the publish failed.
 */
export interface NotificationBlock {
  messageId: string;              // SNS MessageId (proof of fire)
  topicArn: string;
  publishedAt: string;            // ISO-8601 Z
  body: {
    eventType?: string;
    timestamp?: string;
    partnerId?: string;
    taskId?: string;
    file?: {
      filename?: string;
      s3Bucket?: string;
      s3Key?: string;
      sizeBytes?: number;
      checksum?: string;
      contentType?: string;
    };
    metadata?: Record<string, unknown>;   // user-defined — preserved verbatim
    executionId?: string;
    error?: { code?: string; message?: string };  // FILE_FAILED / FILE_QUARANTINED only
    [key: string]: unknown;
  };
  // snake_case by AWS filtering convention — preserved verbatim, do NOT camelCase
  messageAttributes: Record<string, string>;
  sequenceNumber?: string;        // FIFO only, usually absent
}

/** File event for activity feeds and search results */
export interface FileEvent {
  eventId?: string;        // Legacy field
  id?: string;             // New field
  fileId: string;
  filename: string;
  partnerId: string;
  partnerName?: string;
  taskId: string;
  taskName?: string;
  eventType?: FileEventType;
  status: FileStatus | string;
  timestamp: string; // ISO 8601
  sourcePath?: string;
  destinationPath?: string;
  s3Key?: string;
  sizeBytes?: number;
  fileSize?: number;       // Legacy field
  checksum?: string;
  errorMessage?: string;
  metadata?: Record<string, unknown>;
  // THUB-205: exact published SNS notification (or null/undefined when none). Not
  // normalized — see NotificationBlock. Populated on journey events once lane4b's
  // call-site threading lands; null-safe until then.
  notification?: NotificationBlock | null;
}

/** Error details for failed stages */
export interface FileStageError {
  code: string;
  message: string;
  retryable: boolean;
}

/** Individual processing stage */
export interface FileJourneyStage {
  stage: FileStageType;
  status: StageStatus;
  startTime?: string; // ISO 8601
  endTime?: string; // ISO 8601
  duration?: number; // milliseconds
  details?: Record<string, unknown>;
  error?: FileStageError;
}

/** Complete file journey from landing to completion */
export interface FileJourney {
  fileId: string;
  filename: string;
  partnerId: string;
  partnerName?: string;
  taskId: string;
  taskName?: string;
  events?: FileEvent[];      // Legacy field
  stages?: FileJourneyStage[];
  currentStatus?: FileStatus;
  startTime?: string; // ISO 8601
  endTime?: string; // ISO 8601
  totalDuration?: number; // milliseconds
  s3Bucket?: string;
  archivePath?: string;
  originalPath?: string;
  sizeBytes?: number;
  checksum?: string;
  contentType?: string;
}

// Legacy search params (for backward compatibility)
export interface FileEventSearchParams {
  filename?: string;
  partnerId?: string;
  taskId?: string;
  startDate?: string;             // ISO 8601 date string
  endDate?: string;               // ISO 8601 date string
  eventType?: FileEventType;
  limit?: number;
  cursor?: string;
  /**
   * THUB-244: `'file'` returns one row per FILE (its latest event) instead of one row per event.
   * Opt-in, because the Dashboard, Recent Activity and Task-history feeds legitimately want
   * events -- changing the default would silently alter those surfaces.
   */
  groupBy?: 'file';
}

// Legacy search result (for backward compatibility)
export interface FileEventSearchResult {
  events: FileEvent[];
  nextCursor?: string;
  totalCount?: number;
}

// Legacy filter state (for backward compatibility)
export interface FileAuditFilters {
  filename: string;
  partnerId: string;
  taskId: string;
  startDate: string;
  endDate: string;
  eventType: FileEventType | '';
}

// Legacy sort field (for backward compatibility)
export type FileEventSortField = 'timestamp' | 'filename' | 'partnerName' | 'taskName' | 'eventType' | 'status';

/** Date range filter for file audit searches */
export interface FileAuditDateRange {
  start: string; // ISO 8601 date
  end: string; // ISO 8601 date
}

/** Pagination configuration */
export interface PaginationConfig {
  page: number;
  pageSize: number;
  cursor?: string; // For cursor-based pagination
}

/** Sort configuration */
export type FileSortField = 'timestamp' | 'filename' | 'partner' | 'task' | 'status';
export type SortOrder = 'asc' | 'desc';

/** File search query parameters */
export interface FileSearchQuery {
  /** Search by filename (partial match) */
  filename?: string;
  /** Filter by partner ID */
  partnerId?: string;
  /** Filter by task ID */
  taskId?: string;
  /** Filter by status(es) */
  status?: FileStatus[];
  /** Filter by date range */
  dateRange?: FileAuditDateRange;
  /** Pagination */
  pagination: PaginationConfig;
  /** Sort field */
  sortBy: FileSortField;
  /** Sort direction */
  sortOrder: SortOrder;
}

/** File search results */
export interface FileSearchResults {
  items: FileEvent[];
  totalCount: number;
  hasMore: boolean;
  nextCursor?: string;
}

/** Search state for the file audit UI */
export interface FileSearchState {
  query: FileSearchQuery;
  results: FileSearchResults | null;
  isLoading: boolean;
  error: string | null;
}

/** Default search query */
export const DEFAULT_FILE_SEARCH_QUERY: FileSearchQuery = {
  pagination: {
    page: 1,
    pageSize: 25,
  },
  sortBy: 'timestamp',
  sortOrder: 'desc',
};

/** Search validation constraints */
export const SEARCH_CONSTRAINTS = {
  MIN_PAGE_SIZE: 10,
  MAX_PAGE_SIZE: 100,
  DEFAULT_PAGE_SIZE: 25,
  MAX_DATE_RANGE_DAYS: 365,
} as const;

/**
 * Validate search query
 */
export function validateSearchQuery(query: FileSearchQuery): string[] {
  const errors: string[] = [];

  // Validate pagination
  if (query.pagination.pageSize < SEARCH_CONSTRAINTS.MIN_PAGE_SIZE) {
    errors.push(`Page size must be at least ${SEARCH_CONSTRAINTS.MIN_PAGE_SIZE}`);
  }
  if (query.pagination.pageSize > SEARCH_CONSTRAINTS.MAX_PAGE_SIZE) {
    errors.push(`Page size cannot exceed ${SEARCH_CONSTRAINTS.MAX_PAGE_SIZE}`);
  }
  if (query.pagination.page < 1) {
    errors.push('Page number must be at least 1');
  }

  // Validate date range
  if (query.dateRange) {
    const start = new Date(query.dateRange.start);
    const end = new Date(query.dateRange.end);

    if (isNaN(start.getTime())) {
      errors.push('Invalid start date');
    }
    if (isNaN(end.getTime())) {
      errors.push('Invalid end date');
    }
    if (start > end) {
      errors.push('Start date must be before end date');
    }

    const daysDiff = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    if (daysDiff > SEARCH_CONSTRAINTS.MAX_DATE_RANGE_DAYS) {
      errors.push(`Date range cannot exceed ${SEARCH_CONSTRAINTS.MAX_DATE_RANGE_DAYS} days`);
    }
  }

  return errors;
}

/**
 * Get human-readable status label
 */
export function getStatusLabel(status: FileStatus): string {
  const labels: Record<FileStatus, string> = {
    processing: 'Processing',
    ready: 'Ready',
    delivered: 'Delivered',
    picked_up: 'Picked Up',
    failed: 'Failed',
    quarantined: 'Quarantined',
  };
  return labels[status];
}

/**
 * Get stage display name
 */
export function getStageLabel(stage: FileStageType): string {
  const labels: Record<FileStageType, string> = {
    landing: 'Landing',
    processing: 'Processing',
    validation: 'Validation',
    archive: 'Archive',
    notification: 'Notification',
    delivery: 'Delivery',
    pickup: 'Pickup',
    quarantine: 'Quarantine',
  };
  return labels[stage];
}
